import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../api';

const TIPO_CONFIG = {
  entrada:       { label: 'Entrada',       bg: '#d1e7dd', color: '#0a3622', signo: '+' },
  salida:        { label: 'Salida',        bg: '#f8d7da', color: '#721c24', signo: '−' },
  transferencia: { label: 'Transferencia', bg: '#cfe2ff', color: '#084298', signo: '↔' },
};

const fmt = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hoy = () => new Date().toISOString().slice(0, 10);

const Tanques = ({ usuario, onLogout }) => {
  const [tanques, setTanques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vista, setVista] = useState('lista'); // lista | detalle | nuevoTanque
  const [tanqueActual, setTanqueActual] = useState(null);
  const [movimientos, setMovimientos] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Panel de acción en detalle: null | entrada | salida | transferencia | editarTanque
  const [accion, setAccion] = useState(null);

  // Forms
  const [formTanque, setFormTanque] = useState({ nombre: '', capacidad_galones: '', balance_inicial: '', descripcion: '' });
  const [formMov, setFormMov] = useState({ fecha: hoy(), galones: '', cliente: '', factura: '', observaciones: '' });
  const [formTransf, setFormTransf] = useState({ tanque_destino_id: '', fecha: hoy(), galones: '', observaciones: '' });
  const [formEdit, setFormEdit] = useState({ nombre: '', capacidad_galones: '', balance_actual: '', descripcion: '' });

  const esAdmin = usuario?.rol === 'admin';
  const puedeEditar = usuario?.rol === 'admin' || usuario?.rol === 'despachador';

  const cargarTanques = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/tanques');
      setTanques(res.data);
    } catch { setError('Error cargando tanques'); }
    setLoading(false);
  }, []);

  useEffect(() => { cargarTanques(); }, [cargarTanques]);

  const abrirDetalle = async (t) => {
    setTanqueActual(t);
    setError('');
    setAccion(null);
    try {
      const res = await api.get(`/tanques/${t.id}/movimientos`);
      setMovimientos(res.data);
      setVista('detalle');
    } catch { setError('Error cargando movimientos'); }
  };

  const refrescarDetalle = async () => {
    const tRes = await api.get('/tanques');
    setTanques(tRes.data);
    const actualizado = tRes.data.find(x => x.id === tanqueActual.id);
    if (actualizado) setTanqueActual(actualizado);
    const mRes = await api.get(`/tanques/${tanqueActual.id}/movimientos`);
    setMovimientos(mRes.data);
  };

  // ── Crear tanque ────────────────────────────────────────────────────────
  const crearTanque = async () => {
    if (!formTanque.nombre) { setError('El nombre es obligatorio'); return; }
    setError(''); setSaving(true);
    try {
      await api.post('/tanques', {
        nombre: formTanque.nombre,
        capacidad_galones: formTanque.capacidad_galones || null,
        balance_inicial: formTanque.balance_inicial || 0,
        descripcion: formTanque.descripcion || null,
      });
      setFormTanque({ nombre: '', capacidad_galones: '', balance_inicial: '', descripcion: '' });
      await cargarTanques();
      setVista('lista');
    } catch { setError('Error creando tanque'); }
    setSaving(false);
  };

  // ── Entrada / Salida ────────────────────────────────────────────────────
  const registrarMov = async (tipo) => {
    if (!formMov.galones || Number(formMov.galones) <= 0) { setError('Ingresa los galones'); return; }
    setError(''); setSaving(true);
    try {
      await api.post(`/tanques/${tanqueActual.id}/${tipo}`, {
        fecha: new Date(formMov.fecha).toISOString(),
        galones: formMov.galones,
        cliente: formMov.cliente || null,
        factura: formMov.factura || null,
        observaciones: formMov.observaciones || null,
      });
      setFormMov({ fecha: hoy(), galones: '', cliente: '', factura: '', observaciones: '' });
      setAccion(null);
      await refrescarDetalle();
    } catch (e) { setError(e?.response?.data?.detail || 'Error registrando movimiento'); }
    setSaving(false);
  };

  // ── Transferencia ───────────────────────────────────────────────────────
  const registrarTransferencia = async () => {
    if (!formTransf.tanque_destino_id) { setError('Selecciona el tanque destino'); return; }
    if (!formTransf.galones || Number(formTransf.galones) <= 0) { setError('Ingresa los galones'); return; }
    setError(''); setSaving(true);
    try {
      await api.post(`/tanques/${tanqueActual.id}/transferencia`, {
        tanque_destino_id: formTransf.tanque_destino_id,
        fecha: new Date(formTransf.fecha).toISOString(),
        galones: formTransf.galones,
        observaciones: formTransf.observaciones || null,
      });
      setFormTransf({ tanque_destino_id: '', fecha: hoy(), galones: '', observaciones: '' });
      setAccion(null);
      await refrescarDetalle();
    } catch (e) { setError(e?.response?.data?.detail || 'Error en la transferencia'); }
    setSaving(false);
  };

  // ── Editar tanque (corregir balance) ────────────────────────────────────
  const abrirEditar = () => {
    setFormEdit({
      nombre: tanqueActual.nombre,
      capacidad_galones: tanqueActual.capacidad_galones || '',
      balance_actual: tanqueActual.balance_actual,
      descripcion: tanqueActual.descripcion || '',
    });
    setAccion('editarTanque');
  };

  const guardarEdit = async () => {
    setError(''); setSaving(true);
    try {
      await api.put(`/tanques/${tanqueActual.id}`, {
        nombre: formEdit.nombre,
        capacidad_galones: formEdit.capacidad_galones || null,
        balance_actual: formEdit.balance_actual,
        descripcion: formEdit.descripcion || null,
      });
      setAccion(null);
      await refrescarDetalle();
    } catch { setError('Error guardando cambios'); }
    setSaving(false);
  };

  // ── Eliminar movimiento (soft-delete, revierte balance) ─────────────────
  const eliminarMov = async (mov) => {
    if (!window.confirm('¿Eliminar este movimiento? Se revertirá el balance del tanque.')) return;
    setError('');
    try {
      await api.delete(`/tanques/movimiento/${mov.id}`);
      await refrescarDetalle();
    } catch { setError('Error eliminando movimiento'); }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // VISTA: NUEVO TANQUE
  // ═══════════════════════════════════════════════════════════════════════
  if (vista === 'nuevoTanque') {
    return (
      <Layout usuario={usuario} onLogout={onLogout} title="Nuevo Tanque">
        {error && <div style={S.error}>{error}</div>}
        <div style={S.block}>
          <div style={S.form}>
            <div style={S.formGrid}>
              <div style={S.group}>
                <label style={S.label}>Nombre *</label>
                <input style={S.input} defaultValue={formTanque.nombre}
                  onBlur={(e) => setFormTanque({ ...formTanque, nombre: e.target.value })}
                  placeholder="Ej: Tanque 10,000" />
              </div>
              <div style={S.group}>
                <label style={S.label}>Capacidad (gal)</label>
                <input style={S.input} type="number" defaultValue={formTanque.capacidad_galones}
                  onBlur={(e) => setFormTanque({ ...formTanque, capacidad_galones: e.target.value })}
                  placeholder="Opcional" />
              </div>
              <div style={S.group}>
                <label style={S.label}>Balance inicial (gal)</label>
                <input style={S.input} type="number" defaultValue={formTanque.balance_inicial}
                  onBlur={(e) => setFormTanque({ ...formTanque, balance_inicial: e.target.value })}
                  placeholder="0" />
              </div>
              <div style={S.group}>
                <label style={S.label}>Descripción</label>
                <input style={S.input} defaultValue={formTanque.descripcion}
                  onBlur={(e) => setFormTanque({ ...formTanque, descripcion: e.target.value })}
                  placeholder="Opcional" />
              </div>
            </div>
            <div style={S.formBtns}>
              <button style={S.btnCancel} onClick={() => { setVista('lista'); setError(''); }}>Cancelar</button>
              <button style={S.btnSave} onClick={crearTanque} disabled={saving}>
                {saving ? 'Guardando...' : 'Crear tanque'}
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // VISTA: DETALLE (tanque + movimientos + acciones)
  // ═══════════════════════════════════════════════════════════════════════
  if (vista === 'detalle' && tanqueActual) {
    const otrosTanques = tanques.filter(t => t.id !== tanqueActual.id);
    return (
      <Layout usuario={usuario} onLogout={onLogout} title={tanqueActual.nombre}>
        <div style={S.headerRow}>
          <button style={S.btnBack} onClick={() => { setVista('lista'); setAccion(null); }}>← Volver</button>
          {puedeEditar && (
            <div style={S.tabs}>
              <button style={S.btnAccion} onClick={() => { setAccion('entrada'); setError(''); }}>+ Entrada</button>
              <button style={S.btnAccion} onClick={() => { setAccion('salida'); setError(''); }}>− Salida</button>
              <button style={S.btnAccion} onClick={() => { setAccion('transferencia'); setError(''); }}>↔ Transferir</button>
            </div>
          )}
        </div>

        {error && <div style={S.error}>{error}</div>}

        {/* Tarjeta de balance */}
        <div style={S.balanceCard}>
          <div>
            <div style={S.balanceLabel}>Balance actual</div>
            <div style={S.balanceVal}>{fmt(tanqueActual.balance_actual)} gal</div>
            {tanqueActual.capacidad_galones && (
              <div style={S.balanceCap}>Capacidad: {fmt(tanqueActual.capacidad_galones)} gal</div>
            )}
          </div>
          {puedeEditar && (
            <button style={S.btnEdit} onClick={abrirEditar}>✎ Editar / corregir</button>
          )}
        </div>

        {/* Panel de acción */}
        {accion === 'entrada' && (
          <div style={S.addBox}>
            <div style={S.blockTitle}>Registrar entrada</div>
            <MovForm form={formMov} setForm={setFormMov} conCliente />
            <div style={S.formBtns}>
              <button style={S.btnCancel} onClick={() => setAccion(null)}>Cancelar</button>
              <button style={S.btnSave} onClick={() => registrarMov('entrada')} disabled={saving}>Registrar entrada</button>
            </div>
          </div>
        )}

        {accion === 'salida' && (
          <div style={S.addBox}>
            <div style={S.blockTitle}>Registrar salida</div>
            <MovForm form={formMov} setForm={setFormMov} conCliente />
            <div style={S.formBtns}>
              <button style={S.btnCancel} onClick={() => setAccion(null)}>Cancelar</button>
              <button style={S.btnSave} onClick={() => registrarMov('salida')} disabled={saving}>Registrar salida</button>
            </div>
          </div>
        )}

        {accion === 'transferencia' && (
          <div style={S.addBox}>
            <div style={S.blockTitle}>Transferir a otro tanque</div>
            <div style={S.formGrid}>
              <div style={S.group}>
                <label style={S.label}>Tanque destino *</label>
                <select style={S.input} value={formTransf.tanque_destino_id}
                  onChange={(e) => setFormTransf({ ...formTransf, tanque_destino_id: e.target.value })}>
                  <option value="">Seleccionar...</option>
                  {otrosTanques.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
                </select>
              </div>
              <div style={S.group}>
                <label style={S.label}>Fecha *</label>
                <input style={S.input} type="date" defaultValue={formTransf.fecha}
                  onBlur={(e) => setFormTransf({ ...formTransf, fecha: e.target.value })} />
              </div>
              <div style={S.group}>
                <label style={S.label}>Galones *</label>
                <input style={S.input} type="number" defaultValue={formTransf.galones}
                  onBlur={(e) => setFormTransf({ ...formTransf, galones: e.target.value })} placeholder="0.00" />
              </div>
              <div style={S.group}>
                <label style={S.label}>Observaciones</label>
                <input style={S.input} defaultValue={formTransf.observaciones}
                  onBlur={(e) => setFormTransf({ ...formTransf, observaciones: e.target.value })} placeholder="Opcional" />
              </div>
            </div>
            <div style={S.formBtns}>
              <button style={S.btnCancel} onClick={() => setAccion(null)}>Cancelar</button>
              <button style={S.btnSave} onClick={registrarTransferencia} disabled={saving}>Transferir</button>
            </div>
          </div>
        )}

        {accion === 'editarTanque' && (
          <div style={S.addBox}>
            <div style={S.blockTitle}>Editar tanque</div>
            <div style={S.formGrid}>
              <div style={S.group}>
                <label style={S.label}>Nombre</label>
                <input style={S.input} defaultValue={formEdit.nombre}
                  onBlur={(e) => setFormEdit({ ...formEdit, nombre: e.target.value })} />
              </div>
              <div style={S.group}>
                <label style={S.label}>Capacidad (gal)</label>
                <input style={S.input} type="number" defaultValue={formEdit.capacidad_galones}
                  onBlur={(e) => setFormEdit({ ...formEdit, capacidad_galones: e.target.value })} />
              </div>
              <div style={S.group}>
                <label style={S.label}>Balance actual (gal)</label>
                <input style={S.input} type="number" defaultValue={formEdit.balance_actual}
                  onBlur={(e) => setFormEdit({ ...formEdit, balance_actual: e.target.value })} />
              </div>
              <div style={S.group}>
                <label style={S.label}>Descripción</label>
                <input style={S.input} defaultValue={formEdit.descripcion}
                  onBlur={(e) => setFormEdit({ ...formEdit, descripcion: e.target.value })} />
              </div>
            </div>
            <div style={S.hintEdit}>Corregir el balance manualmente no crea un movimiento; úsalo solo para ajustes.</div>
            <div style={S.formBtns}>
              <button style={S.btnCancel} onClick={() => setAccion(null)}>Cancelar</button>
              <button style={S.btnSave} onClick={guardarEdit} disabled={saving}>Guardar</button>
            </div>
          </div>
        )}

        {/* Tabla de movimientos */}
        <div style={S.block}>
          <div style={S.blockHeader}>
            <div style={S.blockTitle}>Movimientos ({movimientos.length})</div>
          </div>
          {movimientos.length === 0 ? (
            <div style={S.emptyMov}>Sin movimientos registrados</div>
          ) : (
            <div style={S.movList}>
              {movimientos.map(m => {
                const cfg = TIPO_CONFIG[m.tipo] || TIPO_CONFIG.entrada;
                return (
                  <div key={m.id} style={S.movRow}>
                    <div style={S.movLeft}>
                      <span style={{ ...S.badge, background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                      <span style={S.movFecha}>{new Date(m.fecha).toLocaleDateString('es-DO')}</span>
                    </div>
                    <div style={S.movMid}>
                      {m.cliente && <div style={S.movCliente}>{m.cliente}</div>}
                      {m.factura && <div style={S.movFactura}>Factura: {m.factura}</div>}
                    </div>
                    <div style={S.movRight}>
                      <div style={{ ...S.movGal, color: cfg.color }}>{cfg.signo} {fmt(m.galones)}</div>
                      <div style={S.movBal}>Bal: {fmt(m.balance_resultante)}</div>
                    </div>
                    {esAdmin && (
                      <button style={S.btnDel} onClick={() => eliminarMov(m)} title="Eliminar">🗑</button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Layout>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // VISTA: LISTA (tarjetas de tanques)
  // ═══════════════════════════════════════════════════════════════════════
  return (
    <Layout usuario={usuario} onLogout={onLogout} title="Inventario de Tanques">
      <div style={S.headerRow}>
        <div style={S.stat}>{tanques.length} tanque(s)</div>
        {puedeEditar && (
          <button style={S.btnAdd} onClick={() => { setVista('nuevoTanque'); setError(''); }}>+ Nuevo Tanque</button>
        )}
      </div>

      {error && <div style={S.error}>{error}</div>}

      {loading ? (
        <div style={S.loading}>Cargando...</div>
      ) : tanques.length === 0 ? (
        <div style={S.empty}>No hay tanques registrados</div>
      ) : (
        <div style={S.grid}>
          {tanques.map(t => (
            <div key={t.id} style={S.tanqueCard} onClick={() => abrirDetalle(t)}>
              <div style={S.tanqueNombre}>{t.nombre}</div>
              <div style={S.tanqueBalance}>{fmt(t.balance_actual)} <span style={S.gal}>gal</span></div>
              {t.capacidad_galones && (
                <div style={S.tanqueCap}>Capacidad: {fmt(t.capacidad_galones)} gal</div>
              )}
              <div style={S.tanqueArrow}>Ver movimientos →</div>
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
};

// Sub-form reutilizable para entrada/salida
const MovForm = ({ form, setForm, conCliente }) => (
  <div style={S.formGrid}>
    <div style={S.group}>
      <label style={S.label}>Fecha *</label>
      <input style={S.input} type="date" defaultValue={form.fecha}
        onBlur={(e) => setForm({ ...form, fecha: e.target.value })} />
    </div>
    <div style={S.group}>
      <label style={S.label}>Galones *</label>
      <input style={S.input} type="number" defaultValue={form.galones}
        onBlur={(e) => setForm({ ...form, galones: e.target.value })} placeholder="0.00" />
    </div>
    {conCliente && (
      <>
        <div style={S.group}>
          <label style={S.label}>Cliente / Proveedor</label>
          <input style={S.input} defaultValue={form.cliente}
            onBlur={(e) => setForm({ ...form, cliente: e.target.value })} placeholder="Opcional" />
        </div>
        <div style={S.group}>
          <label style={S.label}>Factura</label>
          <input style={S.input} defaultValue={form.factura}
            onBlur={(e) => setForm({ ...form, factura: e.target.value })} placeholder="Opcional" />
        </div>
      </>
    )}
    <div style={{ ...S.group, gridColumn: '1 / -1' }}>
      <label style={S.label}>Observaciones</label>
      <input style={S.input} defaultValue={form.observaciones}
        onBlur={(e) => setForm({ ...form, observaciones: e.target.value })} placeholder="Opcional" />
    </div>
  </div>
);

const S = {
  loading: { textAlign: 'center', padding: '60px', color: '#666' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' },
  tabs: { display: 'flex', gap: '10px' },
  stat: { fontSize: '13px', color: '#666', fontWeight: '500' },
  btnAdd: { background: '#CC0000', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 18px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  btnBack: { background: '#fff', color: '#666', border: '1.5px solid #e0e0e0', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  btnAccion: { background: '#fff', color: '#CC0000', border: '1.5px solid #CC0000', borderRadius: '8px', padding: '8px 14px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
  error: { background: '#fff0f0', border: '1px solid #ffcccc', color: '#CC0000', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' },
  empty: { textAlign: 'center', padding: '60px', color: '#999', background: '#fff', borderRadius: '12px', border: '1px solid #e0e0e0' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px' },
  tanqueCard: { background: '#fff', borderRadius: '12px', border: '1px solid #e0e0e0', padding: '18px', cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.05)' },
  tanqueNombre: { fontSize: '14px', fontWeight: '700', color: '#1a1a1a', marginBottom: '8px' },
  tanqueBalance: { fontSize: '26px', fontWeight: '800', color: '#CC0000' },
  gal: { fontSize: '13px', fontWeight: '600', color: '#999' },
  tanqueCap: { fontSize: '12px', color: '#888', marginTop: '4px' },
  tanqueArrow: { fontSize: '12px', color: '#CC0000', fontWeight: '600', marginTop: '12px' },
  balanceCard: { background: '#fff', borderRadius: '12px', border: '1px solid #e0e0e0', padding: '18px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', boxShadow: '0 2px 6px rgba(0,0,0,0.05)' },
  balanceLabel: { fontSize: '12px', color: '#888', fontWeight: '500' },
  balanceVal: { fontSize: '30px', fontWeight: '800', color: '#CC0000', margin: '2px 0' },
  balanceCap: { fontSize: '12px', color: '#888' },
  btnEdit: { background: '#fff', color: '#666', border: '1.5px solid #e0e0e0', borderRadius: '8px', padding: '8px 14px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  addBox: { background: '#f8f8f8', borderRadius: '12px', padding: '18px', marginBottom: '16px' },
  block: { background: '#fff', borderRadius: '12px', border: '1px solid #e0e0e0', padding: '16px', marginBottom: '14px', boxShadow: '0 2px 6px rgba(0,0,0,0.05)' },
  blockHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  blockTitle: { fontSize: '14px', fontWeight: '700', color: '#1a1a1a', marginBottom: '12px' },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },
  group: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '12px', fontWeight: '600', color: '#555' },
  input: { padding: '10px 12px', borderRadius: '7px', border: '1.5px solid #e0e0e0', fontSize: '14px', outline: 'none', color: '#1a1a1a', background: '#fff' },
  hintEdit: { fontSize: '11px', color: '#999', marginTop: '10px', fontStyle: 'italic' },
  formBtns: { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px' },
  btnCancel: { padding: '10px 20px', borderRadius: '7px', border: '1.5px solid #e0e0e0', background: '#fff', color: '#666', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  btnSave: { padding: '10px 20px', borderRadius: '7px', border: 'none', background: '#CC0000', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  emptyMov: { textAlign: 'center', padding: '30px', color: '#999', fontSize: '13px' },
  movList: { display: 'flex', flexDirection: 'column' },
  movRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0', borderTop: '1px solid #f0f0f0' },
  movLeft: { display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '90px' },
  badge: { display: 'inline-block', padding: '3px 8px', borderRadius: '20px', fontSize: '10px', fontWeight: '700', textAlign: 'center' },
  movFecha: { fontSize: '11px', color: '#888' },
  movMid: { flex: 1 },
  movCliente: { fontSize: '13px', fontWeight: '600', color: '#1a1a1a' },
  movFactura: { fontSize: '11px', color: '#888', marginTop: '2px' },
  movRight: { textAlign: 'right', minWidth: '110px' },
  movGal: { fontSize: '15px', fontWeight: '700' },
  movBal: { fontSize: '11px', color: '#888', marginTop: '2px' },
  btnDel: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '15px', opacity: 0.6, padding: '4px' },
};

export default Tanques;
